import {Injectable} from '@angular/core';
import {Router} from '@angular/router';

import {Observable} from 'rxjs';
import {filter, map} from 'rxjs/operators';

import {ESSService} from '../../../+ess/employee-self.service';
import {Employee} from '../../employees/employees.model';
import {RoleRoute} from '../authentication.interfaces';
import {KeycloakWrapperService} from '../keycloak-wrapper.service';

@Injectable()
export class RolesService {

  private routeArray: RoleRoute[] = [
    {role: 'COMPANY_READ', path: '/company/tree'},
    {role: 'COMPANY_WRITE', path: '/company/tree'},
    {role: 'POSTPAY_WRITE', path: '/postpayroll/check'},
    {role: 'POSTPAY_READ', path: '/postpayroll/check'},
    {role: 'POSTPAY_LOCALHR', path: '/postpayroll/check'},
    {role: 'POSTPAY_WRITE_AGGREGATES', path: '/postpayroll/check'},
    {role: 'POSTPAY_READ_KPI', path: '/postpayroll/check'},
    {role: 'POSTPAY_PAYMENTS', path: '/postpayroll/post-prepare'},
    {role: 'POSTPAY_COLLECTIVE_REPORTS', path: '/postpayroll/post-prepare'},
    {role: 'PAYROLL_PAYMENT_READ', path: '/payroll/payment'},
    {role: 'PAYROLL_PAYMENT_WRITE', path: '/payroll/payment'},
    {role: 'PAYROLL_READ', path: '/payroll/finalize'},
    {role: 'PAYROLL_WRITE', path: '/payroll/finalize'},
    {role: 'PAYROLL_PAYMENT_SEPA_READ', path: '/payroll/payment'},
    {role: 'PAYROLL_PAYMENT_SEPA_WRITE', path: '/payroll/payment'},
    {role: 'PAYROLLPREPARE_READ', path: '/payroll/prepare'},
    {role: 'PAYROLLPREPARE_WRITE', path: '/payroll/prepare'},
    {role: 'PEOPLE_TREE_READ', path: '/employees/tree'},
    {role: 'PEOPLE_TREE_WRITE', path: '/employees/tree'},
    {role: 'PEOPLE_READ', path: '/employees/files'},
    {role: 'PEOPLE_WRITE', path: '/employees/files'},
    {role: 'PEOPLE_PARTIAL_UPDATE', path: '/employees/files'},
    {role: 'PEOPLE_SKILL', path: '/employees/skills'},
    {role: 'SETTINGS', path: '/settings/administrate'},
    {role: 'CONFIGURATION', path: '/configuration/rules'},
    {role: 'VARIABLE_SETUP_ADMIN', path: '/settings/variable-setup'},
    {role: 'LIST_TREE_SETUP_ADMIN', path: '/settings/list-setup'},
    {role: 'REPORT_READ', path: '/reports/manage'},
    {role: 'REPORT_WRITE', path: '/reports/manage'},
    {role: 'MASS_BULK_ACTIONS', path: '/mass-data-update/bulk-update'},
    {role: 'MASS_BULK_ACTIONS', path: '/mass-data-update/overview'},
    {role: 'ABSENCE_MASSMENU', path: '/mass-data-update/absence'},
    {role: 'VARIABLE_MASSMENU', path: '/mass-data-update/variable'},
    {role: 'REPOSITORY_READ', path: '/mass-data-update/variable'},
    {role: 'REPOSITORY_WRITE', path: '/mass-data-update/variable'},
    {role: 'PAYROLL_VALIDATE', path: '/payrolls-validation'},
    {role: 'NET_ENTREPRISE_WRITE', path: '/dsn'},
    {role: 'USER_ROLES_ADMIN', path: '/settings/user-rights'},
    {role: 'EMPLOYEE', path: '/inbox/notifications'},
    {role: 'ESS_PERSONAL_INFO', path: '/ess/personal-info'},
    {role: 'ESS_LEAVE', path: '/ess/leave-request'},
    {role: 'ESS_PAYSLIPS', path: '/ess/payslips'},
    {role: 'MSS_PLANNING ', path: '/mss/team-planning'},
    {role: 'MSS_MY_TEAM', path: '/mss/employee-info'},
    {role: 'MSS_MY_TEAM', path: '/mss/backups'},
    {role: 'MSS_REPORTS', path: '/mss/reports'},
    {role: 'MSS_HIRE', path: '/mss/hire'},
    {role: 'ESS_ASK_FOR_VARIABLE', path: '/ess/claims'},
    {role: 'WORKFLOW_ADMIN', path: '/workflows/admin'},
    {role: 'EGEN_DOC_READ', path: '/template-management'},
    {role: 'EGEN_DOC_WRITE', path: '/template-management'},
    {role: 'PEOPLE_READ', path: '/onboarding/overview'}
  ];

  private get roles(): string[] {
    if (this.keycloakWrapperService.isAccessLimited) {
      return ['ACCESS'];
    }
    return this.keycloakWrapperService.tokenParsed
      ? this.keycloakWrapperService.tokenParsed.resource_access.api.roles
      || [] : [];
  }

  constructor(private router: Router,
              private essService: ESSService,
              private keycloakWrapperService: KeycloakWrapperService) {
  }

  public isOnlyNotifications(): boolean {
    return this.roles.length === 1 && this.roles[0] === 'EMPLOYEE';
  }

  public isAccessLimited(): boolean {
    return this.keycloakWrapperService.isAccessLimited;
  }

  public hasRole(...roles: string[]): boolean {
    if (this.keycloakWrapperService.isAccessLimited) {
      return roles.includes('ACCESS');
    }
    return roles.some((role: string) => {
      return this.keycloakWrapperService.hasResourceRole(role, 'api') || this.keycloakWrapperService.hasRealmRole(role);
    });
  }

  public hasRoleByComponent(component: any): boolean | Observable<boolean> {
    const hasRole = this.hasRole(...this.getValidRoles(component.ACCESS_NAME));

    if (component.ACCESS_NAME === 'Dashboard' && !hasRole) {
      const validRoute: string = this.getValidRoute();
      if (validRoute) {
        this.router.navigate([validRoute]);
      }
    }
    if (component.ACCESS_NAME === 'ESSPersonalInfo') {
      return this.essService.currentEmployee
        .pipe(
          filter((employee: Employee): boolean => employee != null),
          map((employee: Employee): boolean => !!employee && hasRole)
        );
    }
    return hasRole;
  }

  private getValidRoute(): string {
    if (this.keycloakWrapperService.isAccessLimited) {
      return '/access-limited';
    }
    const roleRoute: RoleRoute = this.routeArray.find((route) => this.hasRole(route.role));
    return roleRoute ? roleRoute.path : null;
  }

  public getValidRoles(componentName: string): string[] {
    let validRoles: string[] = [];
    switch (componentName) {
      case 'OrgTree':
        validRoles = ['COMPANY_READ', 'COMPANY_WRITE', 'REPOSITORY_READ', 'REPOSITORY_WRITE'];
        break;
      case 'PostPayrollPrepare':
        validRoles = [
          'POSTPAY_WRITE',
          'POSTPAY_PAYMENTS',
          'POSTPAY_COLLECTIVE_REPORTS'
        ];
        break;
      case 'Check':
        validRoles = [
          'POSTPAY_WRITE',
          'POSTPAY_READ',
          'POSTPAY_LOCALHR',
          'POSTPAY_READ_KPI',
          'POSTPAY_WRITE_AGGREGATES'
        ];
        break;
      case 'OngoingPayrolls':
      case 'CalculationEvents':
      case 'PayResults':
        validRoles = ['PAYROLL_READ', 'PAYROLL_WRITE'];
        break;
      case 'PayrollFinalize':
        validRoles = ['PAYROLL_READ', 'PAYROLL_WRITE', 'PAYROLL_FINALIZE'];
        break;
      case 'PayrollPrepare':
        validRoles = ['PAYROLLPREPARE_READ', 'PAYROLLPREPARE_WRITE'];
        break;
      case 'PayrollPayment':
        validRoles = ['PAYROLL_PAYMENT_READ', 'PAYROLL_PAYMENT_WRITE', 'PAYROLL_PAYMENT_SEPA_READ', 'PAYROLL_PAYMENT_SEPA_WRITE'];
        break;
      case 'Dsn':
        validRoles = ['NET_ENTREPRISE_WRITE'];
        break;

      case 'VariableSetup':
        validRoles = ['VARIABLE_SETUP_ADMIN'];
        break;

      case 'ListSetup':
        validRoles = ['LIST_TREE_SETUP_ADMIN'];
        break;

      case 'HireProfile':
        validRoles = ['HIRE_PROFILE_ADMIN'];
        break;

      case 'PayrollsValidation':
        validRoles = ['PAYROLL_VALIDATE'];
        break;
      case 'EmpTree':
        validRoles = ['PEOPLE_READ', 'PEOPLE_WRITE', 'PEOPLE_TREE_READ', 'PEOPLE_TREE_WRITE'];
        break;
      case 'EmployeeFiles':
        validRoles = ['PEOPLE_READ', 'PEOPLE_WRITE', 'PEOPLE_PARTIAL_UPDATE'];
        break;
      case 'EmployeeSkills':
        validRoles = ['PEOPLE_SKILL'];
        break;
      case 'Leave':
        validRoles = ['PEOPLE_TERMINATE'];
        break;
      case 'Hire':
        validRoles = ['PEOPLE_HIRE'];
        break;
      case 'Extend':
        validRoles = ['PEOPLE_EXTEND'];
        break;
      case 'Administration':
        validRoles = ['SETTINGS'];
        break;
      case '':
        validRoles = ['CONFIGURATION'];
        break;
      case 'Dashboard':
        validRoles = ['DASHBOARD'];
        break;
      case 'ReportManage':
        validRoles = ['REPORT_READ', 'REPORT_WRITE'];
        break;
      case 'MduBulkUpdate':
      case 'MduOverview':
      case 'MduBulkUpdateResult':
        validRoles = ['MASS_BULK_ACTIONS'];
        break;
      case 'MduVariable':
        validRoles = ['VARIABLE_MASSMENU', 'VARIABLE_MASSMENU_NODATES'];
        break;
      case 'MduAbsence':
        validRoles = ['ABSENCE_MASSMENU'];
        break;
      case 'ESSPayslips':
        validRoles = ['ESS_PAYSLIPS'];
        break;
      case 'ESSLeaveRequest':
        validRoles = ['ESS_LEAVE'];
        break;
      case 'ESSPersonalInfo':
        validRoles = ['ESS_PERSONAL_INFO'];
        break;
      case 'ESSMyDocuments':
        validRoles = ['ESS_MY_DOCUMENTS'];
        break;
      case 'MSSBackups':
        validRoles = ['MSS_MY_TEAM'];
        break;
      case 'UserRoles':
        validRoles = ['USER_ROLES_ADMIN'];
        break;
      case 'TeamPlanning':
        validRoles = ['MSS_PLANNING'];
        break;
      case 'MyTeam':
        validRoles = ['MSS_MY_TEAM'];
        break;
      case 'MyTeamReports':
        validRoles = ['MSS_REPORTS'];
        break;
      case 'ESSClaims':
        validRoles = ['ESS_ASK_FOR_VARIABLE'];
        break;
      case 'MSSHire':
        validRoles = ['MSS_HIRE'];
        break;
      case 'WorkflowAdmin':
        validRoles = ['WORKFLOW_ADMIN', 'PEOPLE_READ', 'PEOPLE_WRITE'];
        break;
      case 'TemplateManagement':
      case 'GpDocumentEditor':
        validRoles = ['EGEN_DOC_READ', 'EGEN_DOC_WRITE'];
        break;
      case 'OnboardingOverview':
        validRoles = ['ONBOARDING_ADMIN'];
        break;
      default:
        validRoles = [];
        break;
    }

    return validRoles;
  }
}
